import { AxiosInstance } from 'axios';
import { AdminApi } from './api/adminApi';
import { AuthApi } from './api/authApi';
import { CatalogApi } from './api/catalogApi';
import { CustomerApi } from './api/customerApi';
import { DebtApi } from './api/debtApi';
import { ExpenseApi } from './api/expenseApi';
import { createApiHttpClient } from './api/http';
import { InventoryApi } from './api/inventoryApi';
import { SalesApi } from './api/salesApi';
import { PaymentPlanApi } from './api/paymentPlanApi';
import { ReferralApi } from './api/referralApi';
import {
  User,
  Company,
  Customer,
  Item,
  Category,
  AccountMovement,
  Debt,
  Expense,
  LoginRequest,
  RegisterRequest,
  JwtResponse,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CreateUserRequest,
  EmailChangeRequest,
  UpdateUserRequest,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CreateItemRequest,
  UpdateItemRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateDebtRequest,
  UpdateDebtRequest,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  ExpenseSearchParams,
  SearchParams,
  DebtSearchParams,
  PaginatedResponse,
  AddStockRequest,
  RemoveStockRequest,
  AdjustStockRequest,
  InventoryOperationResponse,
  InventoryBatch,
  InventoryTransaction,
  Sale,
  SaleItem,
  SalePayment,
  SalesStatistics,
  AddSaleItemRequest,
  UpdateSaleItemQuantityRequest,
  CompleteSaleRequest,
  CompleteSaleWithPaymentRequest,
  CreateAndCompleteSaleRequest,
  AddPaymentRequest,
  SalesSearchParams,
  TopSellingItem,
  DashboardSummary,
  StockValidationResult,
  BatchStockInfo,
  SubscriptionStatusDto,
  UpdateSubscriptionRequest,
  CompanyExportPreferences,
  CompanyExportDownload,
  ExportPreview,
  CompanyNote,
  PriceEntryMode,
  ReferralCodeResponse,
  ReferralAcquisitionResponse,
  ReferralRewardResponse,
  ReverseReferralRewardRequest,
  CompanyAdminSummary,
  AdminOverview,
  AdminCompanyListPage,
} from '../types';

export class ApiClient {
  private axiosInstance: AxiosInstance;
  private readonly authApi: AuthApi;
  private readonly adminApi: AdminApi;
  private readonly catalogApi: CatalogApi;
  private readonly customerApi: CustomerApi;
  private readonly debtApi: DebtApi;
  private readonly expenseApi: ExpenseApi;
  private readonly inventoryApi: InventoryApi;
  private readonly salesApi: SalesApi;
  readonly paymentPlans: PaymentPlanApi;
  private readonly referralApi: ReferralApi;

  constructor(axiosInstance?: AxiosInstance) {
    this.axiosInstance = createApiHttpClient(axiosInstance);
    this.authApi = new AuthApi(this.axiosInstance);
    this.adminApi = new AdminApi(this.axiosInstance);
    this.catalogApi = new CatalogApi(this.axiosInstance);
    this.customerApi = new CustomerApi(this.axiosInstance);
    this.debtApi = new DebtApi(this.axiosInstance);
    this.expenseApi = new ExpenseApi(this.axiosInstance);
    this.inventoryApi = new InventoryApi(this.axiosInstance);
    this.salesApi = new SalesApi(this.axiosInstance);
    this.paymentPlans = new PaymentPlanApi(this.axiosInstance);
    this.referralApi = new ReferralApi(this.axiosInstance);
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<JwtResponse> {
    return this.authApi.login(credentials);
  }

  async register(userData: RegisterRequest): Promise<void> {
    return this.authApi.register(userData);
  }

  async logout(): Promise<void> {
    return this.authApi.logout();
  }

  async refresh(): Promise<JwtResponse> {
    return this.authApi.refresh();
  }

  async getCurrentUser(): Promise<User> {
    return this.authApi.currentUser();
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.authApi.forgotPassword(email);
  }

  async resendVerification(email: string): Promise<{ message: string }> {
    return this.authApi.resendVerification(email);
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.authApi.resetPassword(token, newPassword);
  }

  async getMyReferralCode(): Promise<ReferralCodeResponse> {
    return this.referralApi.getMyCode();
  }

  async createMyReferralCode(): Promise<ReferralCodeResponse> {
    return this.referralApi.createMyCode();
  }

  // Companies
  async getCompanies(): Promise<Company[]> {
    return this.adminApi.getCompanies();
  }

  async getCompaniesPaginated(params: SearchParams): Promise<PaginatedResponse<Company>> {
    return this.adminApi.getCompaniesPaginated(params);
  }

  async getAdminCompaniesPaginated(params: SearchParams): Promise<AdminCompanyListPage> {
    return this.adminApi.getAdminCompaniesPaginated(params);
  }

  async getCompany(id: string): Promise<Company> {
    return this.adminApi.getCompany(id);
  }

  async getCompanyAdminSummary(id: string, startDate: string, endDate: string): Promise<CompanyAdminSummary> {
    return this.adminApi.getCompanyAdminSummary(id, startDate, endDate);
  }

  async getAdminOverview(startDate: string, endDate: string): Promise<AdminOverview> {
    return this.adminApi.getAdminOverview(startDate, endDate);
  }

  async createCompany(company: CreateCompanyRequest): Promise<Company> {
    return this.adminApi.createCompany(company);
  }

  async updateCompany(id: string, company: UpdateCompanyRequest): Promise<Company> {
    return this.adminApi.updateCompany(id, company);
  }

  async getCurrentCompany(): Promise<Company> {
    return this.adminApi.getCurrentCompany();
  }

  async getCompanyAcquisition(id: string): Promise<ReferralAcquisitionResponse> {
    return this.adminApi.getCompanyAcquisition(id);
  }

  async getCompanyReferralRewards(id: string): Promise<ReferralRewardResponse[]> {
    return this.adminApi.getCompanyReferralRewards(id);
  }

  async reverseReferralReward(id: string, request: ReverseReferralRewardRequest): Promise<ReferralRewardResponse> {
    return this.adminApi.reverseReferralReward(id, request);
  }

  async setReferralCodeActive(codeId: string, active: boolean): Promise<void> {
    return this.adminApi.setReferralCodeActive(codeId, active);
  }

  async updateCurrentPriceEntryMode(priceEntryMode: PriceEntryMode): Promise<Company> {
    return this.adminApi.updateCurrentPriceEntryMode(priceEntryMode);
  }

  async updateCurrentPricingSettings(priceEntryMode: PriceEntryMode, defaultVatRate: number): Promise<Company> {
    return this.adminApi.updateCurrentPricingSettings(priceEntryMode, defaultVatRate);
  }

  async updateCurrentBatchTracking(batchTrackingEnabled: boolean): Promise<Company> {
    return this.adminApi.updateCurrentBatchTracking(batchTrackingEnabled);
  }

  async deleteCompany(id: string): Promise<void> {
    return this.adminApi.deleteCompany(id);
  }

  async getCompanySubscription(id: string): Promise<SubscriptionStatusDto> {
    return this.adminApi.getCompanySubscription(id);
  }

  async updateCompanySubscription(id: string, request: UpdateSubscriptionRequest): Promise<SubscriptionStatusDto> {
    return this.adminApi.updateCompanySubscription(id, request);
  }

  async getCurrentSubscription(): Promise<SubscriptionStatusDto> {
    return this.adminApi.getCurrentSubscription();
  }

  async getCompanyNotes(id: string): Promise<CompanyNote[]> {
    return this.adminApi.getCompanyNotes(id);
  }

  async addCompanyNote(id: string, note: string): Promise<CompanyNote> {
    return this.adminApi.addCompanyNote(id, note);
  }

  async getExportPreferences(): Promise<CompanyExportPreferences> { return this.adminApi.getExportPreferences(); }
  async updateExportPreferences(preferences: Omit<CompanyExportPreferences, 'maxRows'>): Promise<CompanyExportPreferences> { return this.adminApi.updateExportPreferences(preferences); }
  async getExportPreview(): Promise<ExportPreview> { return this.adminApi.getExportPreview(); }
  async exportCompanyData(): Promise<CompanyExportDownload> { return this.adminApi.exportCompanyData(); }

  async searchCompanies(params: SearchParams): Promise<Company[]> {
    return this.adminApi.searchCompanies(params);
  }

  async createCompanyUser(companyId: string, user: CreateUserRequest): Promise<User> {
    return this.adminApi.createCompanyUser(companyId, user);
  }

  // Users
  async getUsers(): Promise<User[]> {
    return this.adminApi.getUsers();
  }

  async getUsersByCompany(companyId: string): Promise<User[]> {
    return this.adminApi.getUsersByCompany(companyId);
  }

  async getUser(id: string): Promise<User> {
    return this.adminApi.getUser(id);
  }

  async createEmployee(user: CreateUserRequest): Promise<User> {
    return this.adminApi.createEmployee(user);
  }

  async updateUser(id: string, user: UpdateUserRequest): Promise<User> {
    return this.adminApi.updateUser(id, user);
  }

  async requestEmailChange(id: string, request: EmailChangeRequest): Promise<User> {
    return this.adminApi.requestEmailChange(id, request);
  }

  async deleteUser(id: string): Promise<void> {
    return this.adminApi.deleteUser(id);
  }

  // Customers
  async getCustomers(): Promise<Customer[]> {
    return this.customerApi.getCustomers();
  }

  async getCustomersPaginated(params: SearchParams): Promise<PaginatedResponse<Customer>> {
    return this.customerApi.getCustomersPaginated(params);
  }

  async getCustomer(id: string): Promise<Customer> {
    return this.customerApi.getCustomer(id);
  }

  async createCustomer(customer: CreateCustomerRequest): Promise<Customer> {
    return this.customerApi.createCustomer(customer);
  }

  async updateCustomer(id: string, customer: UpdateCustomerRequest): Promise<Customer> {
    return this.customerApi.updateCustomer(id, customer);
  }

  async deleteCustomer(id: string): Promise<void> {
    return this.customerApi.deleteCustomer(id);
  }

  async searchCustomers(params: SearchParams): Promise<Customer[]> {
    return this.customerApi.searchCustomers(params);
  }

  // Items
  async getItems(): Promise<Item[]> {
    return this.catalogApi.getItems();
  }

  async getItemsPaginated(params: SearchParams, signal?: AbortSignal): Promise<PaginatedResponse<Item>> {
    return this.catalogApi.getItemsPaginated(params, signal);
  }

  async getItem(id: string): Promise<Item> {
    return this.catalogApi.getItem(id);
  }

  async getItemByBarcode(barcode: string, companyId?: string): Promise<Item> {
    return this.catalogApi.getItemByBarcode(barcode, companyId);
  }

  async createItem(item: CreateItemRequest): Promise<Item> {
    return this.catalogApi.createItem(item);
  }

  async updateItem(id: string, item: UpdateItemRequest): Promise<Item> {
    return this.catalogApi.updateItem(id, item);
  }

  async deleteItem(id: string): Promise<void> {
    return this.catalogApi.deleteItem(id);
  }

  async searchItems(params: SearchParams): Promise<Item[]> {
    return this.catalogApi.searchItems(params);
  }

  async bulkUploadItems(file: File, companyId?: string) {
    return this.catalogApi.bulkUploadItems(file, companyId);
  }

  // Categories
  async getCategories(): Promise<Category[]> {
    return this.catalogApi.getCategories();
  }

  async getCategoriesPaginated(params: SearchParams): Promise<PaginatedResponse<Category>> {
    return this.catalogApi.getCategoriesPaginated(params);
  }

  async getCategory(id: string): Promise<Category> {
    return this.catalogApi.getCategory(id);
  }

  async createCategory(category: CreateCategoryRequest): Promise<Category> {
    return this.catalogApi.createCategory(category);
  }

  async updateCategory(id: string, category: UpdateCategoryRequest): Promise<Category> {
    return this.catalogApi.updateCategory(id, category);
  }

  async deleteCategory(id: string): Promise<void> {
    return this.catalogApi.deleteCategory(id);
  }

  async bulkUploadCustomers(file: File, companyId?: string) {
    return this.customerApi.bulkUploadCustomers(file, companyId);
  }

  // Debts
  async getDebts(): Promise<Debt[]> {
    return this.debtApi.getDebts();
  }

  async getDebtsPaginated(params: SearchParams): Promise<PaginatedResponse<Debt>> {
    return this.debtApi.getDebtsPaginated(params);
  }

  async getDebtsFilteredPaginated(params: DebtSearchParams): Promise<PaginatedResponse<Debt>> {
    return this.debtApi.getDebtsFilteredPaginated(params);
  }

  async getAccountMovements(params: DebtSearchParams, signal?: AbortSignal): Promise<PaginatedResponse<AccountMovement>> {
    return this.debtApi.getAccountMovements(params, signal);
  }

  async getAccountBalanceSummary(companyIdOrSignal?: string | AbortSignal, signal?: AbortSignal) {
    return this.debtApi.getAccountBalanceSummary(companyIdOrSignal, signal);
  }

  async getDashboardSummary(startDate: string, endDate: string, signal?: AbortSignal): Promise<DashboardSummary> {
    const response = await this.axiosInstance.get<DashboardSummary>('/dashboard/summary', {
      params: { startDate, endDate },
      signal,
    });
    return response.data;
  }

  async getAccountMovementsByCustomer(customerId: string): Promise<AccountMovement[]> {
    return this.debtApi.getAccountMovementsByCustomer(customerId);
  }

  async getDebt(id: string): Promise<Debt> {
    return this.debtApi.getDebt(id);
  }

  async getDebtsByCustomer(customerId: string): Promise<Debt[]> {
    return this.debtApi.getDebtsByCustomer(customerId);
  }

  async getCustomerTotalDebt(customerId: string): Promise<{ totalDebt: number }> {
    return this.debtApi.getCustomerTotalDebt(customerId);
  }

  async createDebt(debt: CreateDebtRequest): Promise<Debt> {
    return this.debtApi.createDebt(debt);
  }

  async updateDebt(id: string, debt: UpdateDebtRequest): Promise<Debt> {
    return this.debtApi.updateDebt(id, debt);
  }

  async deleteDebt(id: string): Promise<void> {
    return this.debtApi.deleteDebt(id);
  }

  // Company expenses
  async getExpenses(params: ExpenseSearchParams, signal?: AbortSignal): Promise<PaginatedResponse<Expense>> {
    return this.expenseApi.getExpenses(params, signal);
  }

  async createExpense(request: CreateExpenseRequest): Promise<Expense> {
    return this.expenseApi.createExpense(request);
  }

  async updateExpense(id: string, request: UpdateExpenseRequest): Promise<Expense> {
    return this.expenseApi.updateExpense(id, request);
  }

  async deleteExpense(id: string): Promise<void> {
    return this.expenseApi.deleteExpense(id);
  }

  // Inventory Management Methods
  async addStock(itemId: string, request: AddStockRequest): Promise<InventoryOperationResponse> {
    return this.inventoryApi.addStock(itemId, request);
  }

  async removeStock(itemId: string, request: RemoveStockRequest): Promise<InventoryOperationResponse> {
    return this.inventoryApi.removeStock(itemId, request);
  }

  async adjustStock(itemId: string, request: AdjustStockRequest): Promise<InventoryOperationResponse> {
    return this.inventoryApi.adjustStock(itemId, request);
  }

  async deleteBatch(batchId: string): Promise<InventoryOperationResponse> {
    return this.inventoryApi.deleteBatch(batchId);
  }

  async toggleBatchTracking(itemId: string, enableBatchTracking: boolean, batchNumber?: string): Promise<InventoryOperationResponse> {
    return this.inventoryApi.toggleBatchTracking(itemId, enableBatchTracking, batchNumber);
  }

  async getInventoryBatches(itemId: string): Promise<InventoryBatch[]> {
    return this.inventoryApi.getInventoryBatches(itemId);
  }

  async getInventoryBatchesPaginated(itemId: string, params: SearchParams): Promise<PaginatedResponse<InventoryBatch>> {
    return this.inventoryApi.getInventoryBatchesPaginated(itemId, params);
  }

  async getInventoryTransactions(itemId: string): Promise<InventoryTransaction[]> {
    return this.inventoryApi.getInventoryTransactions(itemId);
  }

  async getInventoryTransactionsPaginated(itemId: string, params: SearchParams): Promise<PaginatedResponse<InventoryTransaction>> {
    return this.inventoryApi.getInventoryTransactionsPaginated(itemId, params);
  }

  async getExpiredBatches(limit?: number, signal?: AbortSignal): Promise<InventoryBatch[]> {
    return this.inventoryApi.getExpiredBatches(limit, signal);
  }

  async getExpiringSoonBatches(daysThreshold = 30, limit?: number, signal?: AbortSignal): Promise<InventoryBatch[]> {
    return this.inventoryApi.getExpiringSoonBatches(daysThreshold, limit, signal);
  }

  // ============================================================================
  // SALES METHODS
  // ============================================================================


  // Get sales with pagination and filtering
  async getSales(params: SalesSearchParams = {}, signal?: AbortSignal): Promise<PaginatedResponse<Sale>> {
    return this.salesApi.getSales(params, signal);
  }

  async getSalePayments(
    params: Pick<SalesSearchParams, 'page' | 'size' | 'startDate' | 'endDate' | 'companyId'>,
    signal?: AbortSignal,
  ): Promise<PaginatedResponse<SalePayment>> {
    return this.salesApi.getSalePayments(params, signal);
  }

  async getTopSellingItemsForRange(
    params: Pick<SalesSearchParams, 'startDate' | 'endDate' | 'companyId'> & { limit?: number },
    signal?: AbortSignal,
  ): Promise<TopSellingItem[]> {
    return this.salesApi.getTopSellingItemsForRange(params, signal);
  }

  // Get sale by ID
  async getSaleById(saleId: string): Promise<Sale> {
    return this.salesApi.getSaleById(saleId);
  }


  // Add item to sale
  async addItemToSale(saleId: string, request: AddSaleItemRequest): Promise<SaleItem> {
    return this.salesApi.addItemToSale(saleId, request);
  }

  // Update sale item quantity
  async updateSaleItemQuantity(saleItemId: string, request: UpdateSaleItemQuantityRequest): Promise<SaleItem> {
    return this.salesApi.updateSaleItemQuantity(saleItemId, request);
  }

  // Remove item from sale
  async removeItemFromSale(saleItemId: string): Promise<void> {
    return this.salesApi.removeItemFromSale(saleItemId);
  }

  // Complete sale
  async completeSale(saleId: string, request: CompleteSaleRequest): Promise<Sale> {
    return this.salesApi.completeSale(saleId, request);
  }

  // Create and complete sale in one step (simplified)
  async createAndCompleteSale(request: CreateAndCompleteSaleRequest): Promise<Sale> {
    return this.salesApi.createAndCompleteSale(request);
  }

  // Complete sale with payment options (legacy - kept for compatibility)
  async completeSaleWithPayment(saleId: string, request: CompleteSaleWithPaymentRequest): Promise<Sale> {
    return this.salesApi.completeSaleWithPayment(saleId, request);
  }

  // Add payment to sale
  async addPaymentToSale(saleId: string, request: AddPaymentRequest): Promise<SalePayment> {
    return this.salesApi.addPaymentToSale(saleId, request);
  }

  // Cancel sale
  async cancelSale(saleId: string, reason?: string): Promise<Sale> {
    return this.salesApi.cancelSale(saleId, reason);
  }

  // Get sales statistics
  async getSalesStatistics(companyId?: string): Promise<SalesStatistics> {
    return this.salesApi.getSalesStatistics(companyId);
  }

  // Get sales by customer
  async getSalesByCustomer(customerId: string, params: SalesSearchParams = {}): Promise<PaginatedResponse<Sale>> {
    return this.salesApi.getSalesByCustomer(customerId, params);
  }

  // Validate stock availability for item
  async validateItemStock(itemId: string, quantity: number, batchId?: string): Promise<StockValidationResult> {
    return this.salesApi.validateItemStock(itemId, quantity, batchId);
  }

  // Get item with current stock info (for sales)
  async getItemForSale(itemId: string): Promise<Item & { availableStock: number; batches?: BatchStockInfo[] }> {
    return this.salesApi.getItemForSale(itemId);
  }

  // Search items for sales (with stock info)
  async searchItemsForSale(searchTerm: string): Promise<(Item & { availableStock: number; batches?: BatchStockInfo[] })[]> {
    return this.salesApi.searchItemsForSale(searchTerm);
  }

  // Get items by category for sales
  async getItemsByCategoryForSale(categoryId: string): Promise<(Item & { availableStock: number; batches?: BatchStockInfo[] })[]> {
    return this.salesApi.getItemsByCategoryForSale(categoryId);
  }

  // Search items for sales
  async searchItemsForSales(searchTerm: string): Promise<Item[]> {
    return this.salesApi.searchItemsForSales(searchTerm);
  }

  // Get available batches for an item (for sales)
  async getAvailableBatches(itemId: string): Promise<BatchStockInfo[]> {
    return this.salesApi.getAvailableBatches(itemId);
  }


  // Get sale receipt data
  async getSaleReceipt(saleId: string): Promise<Sale & { companyInfo: Company; customerInfo: Customer }> {
    return this.salesApi.getSaleReceipt(saleId);
  }

  // Get top selling items
  async getTopSellingItems(limit: number = 10): Promise<{ itemId: string; itemName: string; totalQuantity: number; totalRevenue: number }[]> {
    return this.salesApi.getTopSellingItems(limit);
  }

  // Get sales report data
  async getSalesReport(startDate: string, endDate: string): Promise<{
    totalSales: number;
    totalRevenue: number;
    averageOrderValue: number;
    topCustomers: { customerId: string; customerName: string; totalPurchases: number }[];
    topItems: { itemId: string; itemName: string; totalQuantity: number }[];
    dailySales: { date: string; sales: number; revenue: number }[];
  }> {
    return this.salesApi.getSalesReport(startDate, endDate);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
